/** Maps SR application stages to recruiter-facing hiring funnel labels and colors (Direction B). */

export type FunnelStage =
  | 'lead'
  | 'screening'
  | 'interview'
  | 'offer'
  | 'placed'
  | 'rejected'
  | 'withdrawn';

export const srStageToFunnel: Record<string, FunnelStage> = {
  NEW:            'lead',
  IN_REVIEW:      'screening',
  INTERVIEW:      'interview',
  OFFER:          'offer',
  HIRED:          'placed',
  REJECTED:       'rejected',
  WITHDRAWN:      'withdrawn',
  OFFER_REJECTED: 'withdrawn',
};

export const ACTIVE_STAGES: FunnelStage[] = ['lead', 'screening', 'interview', 'offer'];

/**
 * Stage pill config — Direction B (Moderna) color scheme.
 * Shape: rounded (6px square), not rounded-full.
 * Each stage uses a distinct fill + matching border.
 */
const stageConfig: Record<FunnelStage, { label: string; classes: string }> = {
  lead:      {
    label: 'Lead',
    classes: 'bg-surface-100 text-surface-600 border-surface-200 dark:bg-surface-800/40 dark:text-surface-400 dark:border-surface-700',
  },
  screening: {
    label: 'Screening',
    classes: 'bg-accent-50 text-accent-600 border-accent-200 dark:bg-accent-900/30 dark:text-accent-400 dark:border-accent-700',
  },
  interview: {
    label: 'Interview',
    classes: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700',
  },
  offer:     {
    label: 'Offer',
    classes: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700',
  },
  placed:    {
    label: 'Placed',
    classes: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700',
  },
  rejected:  {
    label: 'Rejected',
    classes: 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-700',
  },
  withdrawn: {
    label: 'Withdrawn',
    classes: 'bg-surface-100 text-surface-500 border-surface-200 dark:bg-surface-800 dark:text-surface-400 dark:border-surface-700',
  },
};

interface FunnelStageBadgeProps {
  /** Raw SR stage string (e.g. "NEW", "IN_REVIEW") or a FunnelStage key. */
  stage: string;
  className?: string;
}

export function FunnelStageBadge({ stage, className = '' }: FunnelStageBadgeProps) {
  const funnelStage: FunnelStage =
    srStageToFunnel[stage?.toUpperCase()] ??
    (stageConfig[stage?.toLowerCase() as FunnelStage]
      ? (stage.toLowerCase() as FunnelStage)
      : 'lead');

  const { label, classes } = stageConfig[funnelStage];

  return (
    <span
      className={`inline-flex items-center rounded border px-2.5 py-0.5 text-xs font-medium ${classes} ${className}`}
    >
      {label}
    </span>
  );
}
